import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Trophy, Target, Award, Calendar, User, Edit } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyMatchReportView({ user }) {
  const { sessionId } = useParams();
  const [report, setReport] = useState(null);
  const [session, setSession] = useState(null);
  const [teamSettings, setTeamSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [sessionId]);

  const fetchReport = async () => {
    try {
      const reportResponse = await axios.get(`${API}/academy/sessions/${sessionId}/match-report`, {
        withCredentials: true
      });
      setReport(reportResponse.data);

      const sessionsResponse = await axios.get(`${API}/academy/sessions`, {
        withCredentials: true
      });
      const foundSession = sessionsResponse.data.find(s => s.id === sessionId);
      setSession(foundSession);

      // Fetch team settings to check privacy controls
      if (foundSession?.team_id) {
        try {
          const teamResponse = await axios.get(`${API}/academy/teams/${foundSession.team_id}`, {
            withCredentials: true
          });
          setTeamSettings({
            show_stats: teamResponse.data.show_stats !== false,
            show_individual_stats: teamResponse.data.show_individual_stats || false
          });
        } catch (err) {
          // Default to showing stats if can't fetch settings
          setTeamSettings({ show_stats: true, show_individual_stats: true });
        }
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error(error.response?.data?.detail || "Failed to load match report");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-[#0a0f1a]">
        <header className="bg-slate-950 border-b border-white/5 shadow-lg">
          <div className="container mx-auto px-4 py-4">
            <Link to="/academy/sessions">
              <Button variant="ghost" className="text-white hover:text-blue-200">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Sessions
              </Button>
            </Link>
          </div>
        </header>
        <div className="container mx-auto px-4 py-12">
          <Card className="p-8 text-center">
            <p className="text-slate-400">No match report found for this session</p>
          </Card>
        </div>
      </div>
    );
  }

  const resultColor = report.result === "win" ? "green" : report.result === "loss" ? "red" : "yellow";
  const resultBg = report.result === "win" ? "bg-emerald-500/10" : report.result === "loss" ? "bg-red-500/10" : "bg-yellow-500/10";
  const resultText = report.result === "win" ? "text-green-300" : report.result === "loss" ? "text-red-300" : "text-yellow-300";
  const resultBorder = report.result === "win" ? "border-emerald-500/30" : report.result === "loss" ? "border-red-500/30" : "border-white/5";
  
  const isAdmin = ["convenor", "captain"].includes(user?.role);
  const isCoach = user?.roles?.includes("academy_coach");
  
  // Debug logging
  console.log('[AcademyMatchReportView] Edit Permission Check:', {
    userId: user?._id,
    userRole: user?.role,
    userRoles: user?.roles,
    reportSubmittedBy: report?.submitted_by,
    isAdmin,
    isCoach,
    matchingIds: report?.submitted_by === user?._id
  });
  
  // Allow editing if:
  // 1. User is admin
  // 2. User is the original submitter (if submitted_by exists)
  // 3. User is a coach and submitted_by is missing (legacy reports)
  const canEdit = isAdmin || 
                  (report?.submitted_by && report.submitted_by === user?._id) || 
                  (isCoach && !report?.submitted_by);

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/academy/sessions">
            <Button variant="ghost" className="text-white hover:text-blue-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Sessions
            </Button>
          </Link>
          {canEdit && (
            <Link to={`/academy/sessions/${sessionId}/match-report`}>
              <Button variant="ghost" className="text-white hover:text-blue-200">
                <Edit className="w-5 h-5 mr-2" />
                Edit Report
              </Button>
            </Link>
          )}
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-white mb-2">Match Report</h2>
          {session && (
            <div className="flex items-center gap-4 text-slate-400">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {new Date(session.session_date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                {session.team_name}
              </div>
            </div>
          )}
        </div>

        {report.type === "match" && (
          <>
            <Card className={`p-6 mb-6 ${resultBg} border-2 ${resultBorder}`}>
              <div className="text-center">
                <h3 className={`text-5xl font-bold ${resultText} uppercase mb-4`}>
                  {report.result}
                </h3>
                <div className="flex items-center justify-center gap-8">
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Our Team</p>
                    <p className="text-4xl font-bold text-white">{report.our_score}</p>
                  </div>
                  <span className="text-3xl text-slate-400">-</span>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Opponent</p>
                    <p className="text-4xl font-bold text-white">{report.opponent_score}</p>
                  </div>
                </div>
                <p className="text-lg text-slate-300 mt-4 font-medium">{report.opponent_name}</p>
              </div>
            </Card>
          </>
        )}

        {report.type === "training" && (
          <Card className="p-6 mb-6 bg-blue-500/10 border-2 border-blue-500/30">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-blue-300 mb-2">Training Session</h3>
              <p className="text-slate-400">Performance data and notes recorded</p>
            </div>
          </Card>
        )}

        <Card className="p-6 mb-6">
          <h3 className="text-xl font-bold text-white mb-4">Player Performance</h3>
          
          {report.player_stats && report.player_stats.length > 0 ? (
            <>
              {/* Check if individual stats should be shown */}
              {isAdmin || isCoach || teamSettings?.show_individual_stats ? (
                <div className="space-y-3">
                  {report.player_stats
                    .filter(stat => stat.goals > 0 || stat.assists > 0 || stat.motm)
                    .map((stat, index) => (
                      <div key={index} className="p-4 bg-[#0a0f1a] rounded-lg border border-white/5">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-white">{stat.player_name || `Player ${stat.player_id}`}</p>
                            {stat.motm && (
                              <span className="flex items-center bg-yellow-500/20 text-yellow-400 text-xs px-2 py-1 rounded-full">
                                <Award className="w-3 h-3 mr-1" />
                                MOTM
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-6">
                          {stat.goals > 0 && (
                            <div className="flex items-center text-sm">
                              <Trophy className="w-4 h-4 text-emerald-400 mr-1" />
                              <span className="font-semibold text-white">{stat.goals}</span>
                              <span className="text-slate-400 ml-1">Goal{stat.goals !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                          {stat.assists > 0 && (
                            <div className="flex items-center text-sm">
                              <Target className="w-4 h-4 text-blue-400 mr-1" />
                              <span className="font-semibold text-white">{stat.assists}</span>
                              <span className="text-slate-400 ml-1">Assist{stat.assists !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Privacy message - No individual stats shown */}
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30 text-center">
                    <p className="text-sm text-blue-300">
                      🔒 <strong>Individual player statistics are hidden for this team.</strong>
                    </p>
                    <p className="text-xs text-blue-400 mt-1">
                      Only team score is shown. Goal scorers, assists, and MOTM are private.
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-slate-400 text-center py-4">No significant player stats recorded</p>
          )}
        </Card>

        {report.match_notes && (
          <Card className="p-6 mb-6">
            <h3 className="text-xl font-bold text-white mb-4">Match Notes</h3>
            <p className="text-slate-300 whitespace-pre-wrap">{report.match_notes}</p>
          </Card>
        )}

        <Card className="p-4 bg-[#0a0f1a] border border-slate-700">
          <div className="flex items-center justify-between text-sm text-slate-400">
            <div>
              <span className="font-semibold">Submitted by:</span> {report.submitted_by_name}
            </div>
            <div>
              <span className="font-semibold">Date:</span>{' '}
              {new Date(report.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
